import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import FilmCard from "@/components/FilmCard";
import ShareButton from "@/components/ShareButton";
import FollowButton from "@/components/FollowButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Globe, Sparkles, Film, Eye, CalendarDays, Clapperboard, Users } from "lucide-react";

const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-ink grain overflow-x-hidden" data-testid={testId}>
    <Navbar />
    {children}
  </div>
);

const Avatar = ({ creator }) => (
  <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden bg-gradient-to-br from-terracotta to-ember flex items-center justify-center text-5xl font-display font-black text-white shrink-0 ring-4 ring-ink shadow-2xl shadow-terracotta/20" data-testid="creator-avatar">
    {creator.has_avatar ? (
      <img
        src={`${API}/users/${creator.id}/avatar${creator.avatar_updated_at ? `?v=${encodeURIComponent(creator.avatar_updated_at)}` : ""}`}
        alt={creator.name}
        className="w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    ) : (
      <span>{creator.name?.[0]?.toUpperCase() || "C"}</span>
    )}
  </div>
);

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="flex items-center gap-2.5" data-testid={testId}>
    <Icon className="w-4 h-4 text-gold" />
    <span className="font-display font-bold text-lg">{value}</span>
    <span className="text-foreground/50 text-sm">{label}</span>
  </div>
);

const ChipGroup = ({ label, items, tone }) => (items || []).length === 0 ? null : (
  <div>
    <p className="text-[11px] uppercase tracking-[0.2em] text-foreground/45 font-semibold mb-2">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {items.map((x) => <span key={x} className={`text-xs px-2.5 py-1 rounded-full border ${tone}`}>{x}</span>)}
    </div>
  </div>
);

const Banner = ({ creator }) => (
  <div className="absolute inset-x-0 top-0 h-[260px] sm:h-[340px] lg:h-[420px]" data-testid="creator-banner">
    <img
      src={`${API}/users/${creator.id}/banner?v=${encodeURIComponent(creator.banner_updated_at || "")}`}
      alt=""
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink" />
  </div>
);

const Glow = () => (
  <>
    <div className="absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full bg-terracotta/15 blur-[120px] pointer-events-none" />
    <div className="absolute top-10 right-0 w-[360px] h-[360px] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
  </>
);

const Header = ({ creator, shareUrl, followers, setFollowers }) => (
  <section className={`relative pb-12 px-6 sm:px-12 lg:px-20 ${creator.has_banner ? "pt-[190px] sm:pt-[250px] lg:pt-[310px]" : "pt-28"}`} data-testid="creator-header">
    {creator.has_banner ? <Banner creator={creator} /> : <Glow />}
    <div className="relative max-w-[1400px] mx-auto flex flex-col md:flex-row gap-8 md:items-end">
      <Avatar creator={creator} />
      <div className="flex-1 min-w-0 fade-up">
        <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-2">Filmmaker on Cinevoya</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] break-words" data-testid="creator-name">{creator.name}</h1>
        <p className="font-mono text-gold/90 mt-2 text-sm" data-testid="creator-handle">@{creator.handle}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
          <Stat icon={Film} value={creator.film_count} label={creator.film_count === 1 ? "film" : "films"} testId="creator-film-count" />
          <Stat icon={Users} value={followers.toLocaleString()} label={followers === 1 ? "follower" : "followers"} testId="creator-follower-count" />
          <Stat icon={Eye} value={(creator.total_views || 0).toLocaleString()} label="views" testId="creator-total-views" />
          {creator.joined_at && <Stat icon={CalendarDays} value={new Date(creator.joined_at).getFullYear()} label="joined" testId="creator-joined" />}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 self-start md:self-end">
        <FollowButton handle={creator.handle} onCountChange={setFollowers} testId="creator-follow-btn" />
        <ShareButton
          url={shareUrl}
          title={`${creator.name} · Cinevoya`}
          text={`Watch short films by ${creator.name} on Cinevoya — short films from Africa.`}
          className="bg-terracotta hover:bg-ember text-white border-0 h-11 px-6 rounded-full"
          variant="default"
          testId="creator-share-btn"
        />
      </div>
    </div>
  </section>
);

const About = ({ creator }) => (
  <aside className="space-y-6 lg:sticky lg:top-28 self-start" data-testid="creator-about">
    {creator.bio ? (
      <p className="text-foreground/80 leading-relaxed whitespace-pre-line" data-testid="creator-bio">{creator.bio}</p>
    ) : (
      <p className="text-foreground/40 text-sm italic" data-testid="creator-bio-empty">This filmmaker hasn&apos;t written a bio yet.</p>
    )}
    <div className="flex flex-wrap gap-2">
      {creator.available_for_work && (
        <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 gap-1"><Sparkles className="w-3 h-3" /> Available for work</Badge>
      )}
      {creator.location && <Badge className="bg-white/5 border border-white/10 gap-1"><MapPin className="w-3 h-3" /> {creator.location}</Badge>}
      {creator.website && (
        <a href={creator.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-terracotta hover:text-ember transition-colors" data-testid="creator-website-link">
          <Globe className="w-3 h-3" /> Website
        </a>
      )}
    </div>
    <ChipGroup label="Roles" items={creator.roles} tone="bg-terracotta/15 text-terracotta border-terracotta/30" />
    <ChipGroup label="Skills" items={creator.skills} tone="bg-white/5 border-white/10" />
    <ChipGroup label="Genres" items={creator.genre_preferences} tone="bg-gold/10 text-gold border-gold/30" />
  </aside>
);

const Catalogue = ({ films }) => (
  <div data-testid="creator-catalogue">
    <div className="flex items-baseline justify-between mb-6">
      <h2 className="font-display text-2xl sm:text-3xl font-bold">Film catalogue</h2>
      <span className="text-sm text-foreground/50">{films.length} on Cinevoya</span>
    </div>
    {films.length === 0 ? (
      <div className="p-10 rounded-2xl border border-dashed border-white/10 text-center" data-testid="creator-catalogue-empty">
        <Clapperboard className="w-10 h-10 mx-auto text-foreground/20 mb-3" />
        <p className="text-foreground/60">No films published yet — check back soon.</p>
      </div>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
        {films.map((f, i) => <FilmCard key={f.id} film={f} index={i} fluid />)}
      </div>
    )}
  </div>
);

const NotFound = ({ message }) => (
  <Shell testId="creator-not-found">
    <div className="pt-40 px-8 text-center">
      <p className="font-display text-3xl font-bold mb-3">{message}</p>
      <p className="text-foreground/60 mb-6">The link may be mistyped, or the filmmaker changed their handle.</p>
      <Link to="/browse"><Button className="bg-terracotta hover:bg-ember text-white rounded-full" data-testid="creator-not-found-browse-btn">Browse films</Button></Link>
    </div>
  </Shell>
);

export default function CreatorProfile() {
  const { handle: raw = "" } = useParams();
  const isHandle = raw.startsWith("@") && raw.length > 1;
  const handle = raw.slice(1);
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  const [followers, setFollowers] = useState(0);

  useEffect(() => {
    if (!isHandle) return;
    setCreator(null);
    setError(null);
    api.get(`/filmmakers/${encodeURIComponent(handle)}`)
      .then((r) => { setCreator(r.data); setFollowers(r.data.follower_count || 0); document.title = `${r.data.name} · Cinevoya`; })
      .catch(() => setError("Filmmaker not found"));
  }, [handle, isHandle]);

  if (!isHandle) return <NotFound message="Page not found" />;
  if (error) return <NotFound message={error} />;
  if (!creator) return <Shell><div className="pt-40 text-center text-foreground/60" data-testid="creator-loading">Loading…</div></Shell>;

  const shareUrl = `${window.location.origin}/@${creator.handle}`;
  return (
    <Shell testId="creator-profile">
      <Header creator={creator} shareUrl={shareUrl} followers={followers} setFollowers={setFollowers} />
      <div className="px-6 sm:px-12 lg:px-20 pb-24">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-12 border-t border-white/5 pt-12">
          <About creator={creator} />
          <Catalogue films={creator.filmography || []} />
        </div>
      </div>
    </Shell>
  );
}
